"use client";
import { useEffect, useMemo, useState } from "react";
import { filter as lodashFilter } from "lodash";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Input } from "@/app/admin/_common/ui/input";
import GuestTable from "@/app/admin/_common/guestlist/GuestTable";
import GuestTableSkeleton from "@/app/admin/_common/guestlist/GuestTableSkeleton";
import ShowService from "@/app/_services/ShowService";
import FestivalService from "@/app/_services/FestivalService";
import type { FestivalDetailShowRow } from "@/app/_services/FestivalService";
import { ChevronDown, Search } from "lucide-react";

const tableContainerClassName =
  "flex max-h-[calc(100vh-260px)] flex-col overflow-hidden rounded-md border border-border";

export default function FestivalGuestsSection({
  shows,
  festivalId,
}: {
  shows: FestivalDetailShowRow[];
  festivalId: string;
}) {
  const [futureShows, setFutureShows] = useState<any[]>([]);
  const [guests, setGuests] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState<string[]>([]);

  useEffect(() => {
    ShowService.getShow({ future: true }).then((res) => setFutureShows(Array.isArray(res) ? res : []));
  }, []);

  async function loadGuests() {
    setIsLoading(true);
    try {
      const res = await FestivalService.getFestivalGuestsAsync(festivalId);
      setGuests(Array.isArray(res) ? res : []);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [festivalId]);

  const filteredGuests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lodashFilter(guests, (order) => {
      if (showFilter.length > 0 && !showFilter.includes(order.attending_show_data?.id)) return false;
      if (!q) return true;
      const guest = order.guest_data ?? {};
      return (
        `${guest.first_name ?? ""} ${guest.last_name ?? ""}`.toLowerCase().includes(q) ||
        guest.email?.toLowerCase().includes(q) ||
        guest.instagram?.toLowerCase().includes(q)
      );
    });
  }, [guests, search, showFilter]);

  if (shows.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-center text-[13px] text-muted-foreground">
        Add shows to this festival to see guest lists.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-[320px] flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Search className="size-3.5 text-muted-foreground" />
          </div>
          <Input
            type="search"
            placeholder="Search guests…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[30px] pl-9 text-[12.5px]"
            aria-label="Search guests"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-3 text-[12.5px] text-foreground hover:bg-muted"
            >
              <span className="text-muted-foreground">Show:</span>
              {showFilter.length === 0 ? "All" : `${showFilter.length} selected`}
              <ChevronDown className="size-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {shows.map((show) => (
              <DropdownMenuCheckboxItem
                key={show.id}
                checked={showFilter.includes(show.id)}
                onCheckedChange={(checked) =>
                  setShowFilter((prev) =>
                    checked ? [...prev, show.id] : prev.filter((id) => id !== show.id)
                  )
                }
                onSelect={(e) => e.preventDefault()}
              >
                {show.title}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <GuestTableSkeleton showColumn />
      ) : (
        <GuestTable
          guests={filteredGuests}
          liveCheckIn={false}
          showColumn
          futureShows={futureShows}
          containerClassName={tableContainerClassName}
          onChanged={loadGuests}
        />
      )}
    </div>
  );
}
